import { Refine, useIsAuthenticated } from '@refinedev/core';
import { domAnimation, LazyMotion } from 'framer-motion';
import type { ReactNode } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { accessControlProvider } from './auth/accessControlProvider';
import { authProvider } from './auth/authProvider';
import { BrandingProvider } from './branding/BrandingProvider';
import { Layout } from './components/Layout';
import { dataProvider } from './data/dataProvider';
import { HierarchyProvider } from './hierarchy/HierarchyProvider';
import { liveProvider } from './live/liveProvider';
import { BackendStatusPage } from './pages/BackendStatusPage';
import { AccountPage } from './pages/AccountPage';
import { InstallCodePage } from './pages/InstallCodePage';
import { LoginPage } from './pages/LoginPage';
import { NodeDetailPage } from './pages/NodeDetailPage';
import { NodesPage } from './pages/NodesPage';
import { SessionsPage } from './pages/SessionsPage';
import { TerminalStandalonePage } from './pages/TerminalStandalonePage';
import { TopologyPage } from './pages/TopologyPage';
import { AuditPage } from './pages/admin/AuditPage';
import { BrandingPage } from './pages/admin/BrandingPage';
import { EnrollmentPage } from './pages/admin/EnrollmentPage';
import { HierarchyAdminPage } from './pages/admin/HierarchyAdminPage';
import { NotificationChannelsPage } from './pages/admin/NotificationChannelsPage';
import { PluginsPage } from './pages/admin/PluginsPage';
import { ScheduledTasksPage } from './pages/admin/ScheduledTasksPage';
import { UsersPage } from './pages/admin/UsersPage';

/**
 * El panel es un cliente de la puerta (P2), igual que el servidor MCP. Todo lo
 * que muestra o dispara pasa por el backend, que autoriza y audita.
 *
 * El guardián de rutas es propio y no el `<Authenticated>` de Refine porque el
 * panel no monta `routerProvider`: la navegación la lleva react-router directo
 * y Refine se usa por sus proveedores (datos, auth, acceso, tiempo real).
 *
 * **No hay guardián por permiso en las rutas de administración, y es a propósito.**
 * Una ruta que redirige a quien no puede entrar convierte un 403 explicado en una
 * pantalla que se esfuma sin decir nada. Cada pantalla comprueba su permiso, pinta
 * el motivo y deja los botones apagados; quien decide de verdad es el backend, que
 * responderá 403 aunque el panel se equivoque.
 */

function RequireAuth({ children }: { children: ReactNode }) {
  const { data, isLoading } = useIsAuthenticated();

  if (isLoading) return <p className="p-6 text-sm opacity-60">Comprobando sesión…</p>;
  if (!data?.authenticated) return <Navigate to="/login" replace />;

  // La jerarquía y el canal en vivo solo tienen sentido con sesión: montarlos
  // aquí dentro evita abrir el WebSocket en la pantalla de login.
  return <HierarchyProvider>{children}</HierarchyProvider>;
}

function LoginRoute() {
  const { data, isLoading } = useIsAuthenticated();
  if (isLoading) return null;
  if (data?.authenticated) return <Navigate to="/" replace />;
  return <LoginPage />;
}

export function App() {
  return (
    /*
     * `LazyMotion` + `strict` una sola vez, en la raíz: todo lo que anima
     * (Modal, Toasts, el sidebar, `FloatingPaths` del login) importa `m` de
     * `framer-motion` en vez de `motion`, y `strict` avisa en consola si algún
     * componente se cuela con `motion.*` sin querer. El motor de animación
     * (`domAnimation`, sin drag ni layout) se carga una vez para todo el panel
     * en vez de una vez por componente que anime — es la diferencia entre
     * "framer-motion en media docena de sitios" y "media docena de copias del
     * motor de framer-motion".
     */
    <LazyMotion features={domAnimation} strict>
      <BrowserRouter>
        {/*
          El branding envuelve el login además del panel: un tenant con marca propia
          tiene que verla desde la pantalla de entrada, que es la primera que ve un
          cliente al abrir su dominio.
        */}
        <BrandingProvider>
          <Refine
            dataProvider={dataProvider}
            authProvider={authProvider}
            accessControlProvider={accessControlProvider}
            liveProvider={liveProvider}
            resources={[
              { name: 'clients', list: '/admin/jerarquia', meta: { label: 'Clientes' } },
              { name: 'branches', list: '/admin/jerarquia', meta: { label: 'Sucursales' } },
              { name: 'nodes', list: '/', show: '/nodes/:id', meta: { label: 'Nodos' } },
              { name: 'users', list: '/admin/usuarios', meta: { label: 'Usuarios' } },
              { name: 'audit', list: '/admin/bitacora', meta: { label: 'Bitácora' } },
              {
                name: 'notification-channels',
                list: '/admin/notificaciones',
                meta: { label: 'Notificaciones' },
              },
              {
                name: 'scheduled-tasks',
                list: '/admin/tareas-programadas',
                meta: { label: 'Tareas programadas' },
              },
              { name: 'sessions', list: '/sesiones', meta: { label: 'Sesiones' } },
            ]}
            options={{ disableTelemetry: true, warnWhenUnsavedChanges: true, liveMode: 'auto' }}
          >
            <Routes>
              <Route path="/login" element={<LoginRoute />} />
              {/*
                La página de instalación por código (plan de enrolamiento por
                código, §7). **Pública y fuera de `RequireAuth` a propósito**:
                quien la abre es la caja del cliente, que no tiene sesión del
                panel ni debe tenerla — abrir el panel ahí para copiar un token
                es exactamente lo que el plan quita de en medio.

                Tampoco redirige a quien sí tiene sesión, al revés que `/login`:
                el técnico que necesita el comando de una caja con Windows 7 abre
                esta misma URL en su portátil, y ahí sí hay sesión abierta.
              */}
              <Route path="/i" element={<InstallCodePage />} />
              {/*
                Fuera de `<Layout>` a propósito: es la ventana que abre el botón
                "Abrir en otra ventana" de la terminal, y tiene que verse como una
                terminal sola, no como el panel entero encogido.
              */}
              <Route
                path="/nodes/:nodeId/terminal"
                element={
                  <RequireAuth>
                    <TerminalStandalonePage />
                  </RequireAuth>
                }
              />
              <Route
                path="*"
                element={
                  <RequireAuth>
                    <Layout>
                      <Routes>
                        <Route path="/" element={<NodesPage />} />
                        <Route path="/nodes/:id" element={<NodeDetailPage />} />
                        <Route path="/topology" element={<TopologyPage />} />
                        <Route path="/topology/:clientId" element={<TopologyPage />} />
                        <Route path="/sesiones" element={<SessionsPage />} />
                        <Route path="/admin/jerarquia" element={<HierarchyAdminPage />} />
                        <Route path="/admin/enrolar" element={<EnrollmentPage />} />
                        <Route path="/admin/usuarios" element={<UsersPage />} />
                        <Route path="/admin/plugins" element={<PluginsPage />} />
                        <Route path="/admin/notificaciones" element={<NotificationChannelsPage />} />
                        <Route path="/admin/tareas-programadas" element={<ScheduledTasksPage />} />
                        <Route path="/admin/bitacora" element={<AuditPage />} />
                        <Route path="/admin/branding" element={<BrandingPage />} />
                        <Route path="/estado" element={<BackendStatusPage />} />
                        <Route path="/cuenta" element={<AccountPage />} />
                        <Route path="*" element={<Navigate to="/" replace />} />
                      </Routes>
                    </Layout>
                  </RequireAuth>
                }
              />
            </Routes>
          </Refine>
        </BrandingProvider>
      </BrowserRouter>
    </LazyMotion>
  );
}
